//@ts-nocheck
import { Decimal } from 'decimal.js';

export const getAddOnPricePercent = (addon = {}) =>
  addon?.pricePercent ?? addon?.pricepercent ?? addon?.price_percent ?? null;

export const hasPercentagePrice = (value) =>
  value !== null && value !== undefined;

export const matchesId = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  String(left) === String(right);

export const isPercentageBasedAddOn = (
  addon = {},
  enablePercentageBasedAddons = false
) =>
  Boolean(enablePercentageBasedAddons) &&
  hasPercentagePrice(getAddOnPricePercent(addon));

// Keep invalid pricing non-finite so checkout validation can reject it.
const toDecimal = (value) =>
  new Decimal(
    Number.isFinite(Number(value ?? 0)) ? String(value ?? 0).trim() || 0 : NaN
  );

export const roundPercentageAddOnAmount = (amount) =>
  toDecimal(amount).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();

export const calculatePercentageAddOnAmount = (
  passengerPlanPrice = 0,
  pricePercent = 0
) =>
  // Match the backend: decimal multiplication, rounded once per passenger.
  roundPercentageAddOnAmount(
    toDecimal(passengerPlanPrice).mul(toDecimal(pricePercent).div(100))
  );

export const getSelectedAddOnPassengerIds = ({
  selectedAddOns = [],
  tripaddonid
}) => {
  const ids = selectedAddOns
    .filter((selected) => matchesId(selected.tripaddonid, tripaddonid))
    .flatMap((selected) => selected.passengerFormIds || []);
  return Array.from(new Map(ids.map((id) => [String(id), id])).values());
};

export const getPassengerPlan = (passenger = {}, bookingPlans = []) =>
  bookingPlans.find(
    (plan) =>
      Number(plan?.tripplanid) === Number(passenger?.tripplanid) ||
      Number(plan?.tripplan?.tripplanid) === Number(passenger?.tripplanid)
  ) || null;

const resolvePassengerPlanPrice = (passenger = {}, bookingPlans = []) => {
  if (
    passenger?.passengerPlanPrice !== null &&
    passenger?.passengerPlanPrice !== undefined
  ) {
    return passenger.passengerPlanPrice;
  }

  const plan = getPassengerPlan(passenger, bookingPlans);
  return plan?.tripplan?.priceperperson ?? plan?.priceperperson ?? null;
};

export const getPassengerPlanPrice = (passenger = {}, bookingPlans = []) =>
  Number(resolvePassengerPlanPrice(passenger, bookingPlans) ?? 0);

export const getPassengerPlanTitle = (passenger = {}, bookingPlans = []) => {
  if (passenger?.passengerPlanTitle || passenger?.passengerPlanName) {
    return passenger.passengerPlanTitle ?? passenger.passengerPlanName;
  }

  const plan = getPassengerPlan(passenger, bookingPlans);
  return plan?.title ?? plan?.tripplan?.title ?? '';
};

export const getPassengerAddOnAmount = ({
  addon,
  passenger,
  bookingPlans = [],
  lockedAmount = null
}) => {
  if (lockedAmount !== null && lockedAmount !== undefined) {
    return Number(lockedAmount || 0);
  }

  // The flag controls authoring. A stored percentage still determines the
  // passenger's price if flag evaluation is unavailable during checkout.
  if (hasPercentagePrice(getAddOnPricePercent(addon))) {
    return calculatePercentageAddOnAmount(
      getPassengerPlanPrice(passenger, bookingPlans),
      getAddOnPricePercent(addon)
    );
  }

  return Number(addon?.priceperperson ?? addon?.pricePerPerson ?? 0);
};

export const getPassengerTripAddOnIds = ({
  selectedAddOns = [],
  passenger = {}
}) =>
  Array.from(
    new Set(
      selectedAddOns
        .filter((selected) =>
          (selected?.passengerFormIds || []).some(
            (id) =>
              matchesId(passenger?.formId, id) || matchesId(passenger?.id, id)
          )
        )
        .map((selected) => Number(selected?.tripaddonid))
        .filter((id) => Number.isInteger(id) && id > 0)
    )
  );

const getSelectedAddOnAssignments = ({
  selectedAddOns = [],
  addons = [],
  passengers = [],
  bookingPlans = [],
  validate = false
}) => {
  const assignments = [];
  const seen = new Set();

  for (const selected of selectedAddOns) {
    const addon = addons.find((item) =>
      matchesId(item?.tripaddonid, selected?.tripaddonid)
    );
    if (!addon) {
      if (validate) throw new Error('Selected add-on could not be resolved');
      continue;
    }

    for (const id of selected?.passengerFormIds || []) {
      const passenger = passengers.find(
        (item) => matchesId(item?.formId, id) || matchesId(item?.id, id)
      );
      const key = JSON.stringify([
        String(addon.tripaddonid),
        String(passenger?.formId ?? passenger?.id ?? id)
      ]);
      if (seen.has(key)) continue;
      seen.add(key);

      if (validate) {
        if (!passenger) {
          throw new Error('Selected add-on passenger could not be resolved');
        }
        if (hasPercentagePrice(getAddOnPricePercent(addon))) {
          const planPrice = resolvePassengerPlanPrice(passenger, bookingPlans);
          if (
            planPrice === null ||
            planPrice === '' ||
            !Number.isFinite(Number(planPrice)) ||
            !Number.isFinite(Number(getAddOnPricePercent(addon)))
          ) {
            throw new Error('Percentage add-on pricing could not be resolved');
          }
        }
      }

      const amount = getPassengerAddOnAmount({
        addon,
        passenger,
        bookingPlans
      });
      if (validate && !Number.isFinite(amount)) {
        throw new Error('Selected add-on price is invalid');
      }
      assignments.push({ addon, passenger, amount });
    }
  }

  return assignments;
};

export const getCheckoutAddOns = (input = {}) =>
  getSelectedAddOnAssignments({ ...input, validate: true }).map(
    ({ addon, amount }) => ({
      pricePerPerson: amount,
      dueAtBooking: addon?.dueatbooking ?? addon?.dueAtBooking ?? false
    })
  );

export const getSelectedAddOnsTotal = ({
  selectedAddOns = [],
  addons = [],
  passengers = [],
  bookingPlans = []
}) =>
  getSelectedAddOnAssignments({
    selectedAddOns,
    addons,
    passengers,
    bookingPlans
  }).reduce((total, assignment) => total + assignment.amount, 0);

export const groupSelectedAddOnSummaryItems = ({
  selectedAddOns = [],
  addons = [],
  passengers = [],
  bookingPlans = [],
  getPassengerLabel = (count) => `${count} passenger`
}) => {
  const grouped = new Map();
  getSelectedAddOnAssignments({
    selectedAddOns,
    addons,
    passengers,
    bookingPlans
  }).forEach(({ addon, amount }) => {
    const isPercentage = hasPercentagePrice(getAddOnPricePercent(addon));
    const key = JSON.stringify([
      String(addon.tripaddonid),
      isPercentage ? amount : null
    ]);
    if (!grouped.has(key)) {
      grouped.set(key, {
        addon,
        passengerCount: 0,
        amount: 0,
        ...(isPercentage ? { perPersonAmount: amount } : {})
      });
    }
    const item = grouped.get(key);
    item.passengerCount += 1;
    item.amount += amount;
  });

  return Array.from(grouped.values()).map((item) => ({
    ...item,
    title: `${item.addon?.title || ''} (${getPassengerLabel(
      item.passengerCount
    )})`
  }));
};
